<template>
  <div class="mov-page">
    <!-- Header -->
    <div class="page-header mb-4">
      <div class="d-flex align-center">
        <div class="header-icon-pill entrada mr-3">
          <v-icon color="white" size="22">mdi-cash-plus</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-0">Entradas de Saldo</h1>
          <p class="page-subtitle mb-0">Registro de abonos recibidos</p>
        </div>
      </div>
    </div>

    <!-- Tabs modernos -->
    <div class="tabs-pill mb-4">
      <button :class="['tab-btn', tab === 0 && 'active-entrada']" @click="tab=0; limpiar()">
        <v-icon size="16" class="mr-1">mdi-format-list-bulleted</v-icon>Registradas
      </button>
      <button :class="['tab-btn', tab === 1 && 'active-entrada']" @click="tab=1; limpiar()">
        <v-icon size="16" class="mr-1">mdi-plus-circle</v-icon>Nueva Entrada
      </button>
    </div>

    <!-- TAB 0 — Historial -->
    <div v-if="tab === 0">
      <v-card class="glass-card rounded-xl overflow-hidden" elevation="0">
        <div class="d-flex align-center px-5 pt-5 pb-3 flex-wrap" style="gap:12px;">
          <div class="section-pill entrada-pill mr-2">
            <v-icon color="white" size="14">mdi-history</v-icon>
          </div>
          <span class="section-title">Historial</span>
          <v-spacer />
          <!-- Fecha -->
          <v-dialog ref="dialog" v-model="dateModal" :return-value.sync="dates" persistent width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="datesFormat" label="Rango de fechas" prepend-inner-icon="mdi-calendar"
                readonly outlined dense rounded hide-details class="modern-input" style="max-width:220px;"
                v-bind="attrs" v-on="on" />
            </template>
            <v-date-picker v-model="dates" range>
              <v-spacer />
              <v-btn text color="primary" @click="dateModal=false">Cancelar</v-btn>
              <v-btn text color="primary" @click="$refs.dialog.save(dates); setDatesFormat()">OK</v-btn>
            </v-date-picker>
          </v-dialog>
          <v-btn color="primary" rounded elevation="0" class="font-weight-bold px-4" @click="getData()" :disabled="!valid">
            <v-icon left size="16">mdi-magnify</v-icon>Buscar
          </v-btn>
          <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Filtrar..." outlined dense rounded hide-details
            class="modern-input" style="max-width:200px;" />
        </div>
        <v-divider style="opacity:0.08;" class="mx-5" />
        <v-data-table :headers="headers" :items="items" :search="search" class="modern-table"
          no-data-text="Sin registros para el período seleccionado">
          <template v-slot:[`item.monto`]="{ item }">
            <span class="monto-entrada font-weight-bold">₡{{ fmt(item.monto) }}</span>
          </template>
          <template v-slot:[`item.fecha`]="{ item }">
            <span class="text-caption">{{ fmtFecha(item.fecha) }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="primary" @click="editItem(item)">
              <v-icon size="16">mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- TAB 1 — Nueva entrada -->
    <div v-if="tab === 1">
      <v-card class="glass-card rounded-xl pa-6" elevation="0">
        <div class="d-flex align-center mb-5">
          <div class="section-pill entrada-pill mr-2">
            <v-icon color="white" size="14">mdi-cash-plus</v-icon>
          </div>
          <span class="section-title">{{ editedIndex > -1 ? 'Editar Entrada' : 'Nueva Entrada' }}</span>
        </div>
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" sm="4">
              <v-select label="Vendedor" item-text="nombre" item-value="cedula"
                v-model="editedItem.idUsuario" :items="usuarios" :disabled="!validUsuarios || incluirValid"
                outlined rounded dense hide-details="auto" class="modern-input"
                prepend-inner-icon="mdi-account" />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field v-model="editedItem.monto" type="number" label="Monto" step="any" min="0"
                required outlined rounded dense hide-details="auto" class="modern-input" :disabled="incluirValid"
                prepend-inner-icon="mdi-currency-usd" />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field type="text" v-model="fecha" label="Fecha" readonly outlined rounded dense
                hide-details="auto" class="modern-input" prepend-inner-icon="mdi-calendar" />
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field v-model="editedItem.descripcion" label="Descripción" :rules="nameRules"
                required outlined rounded dense hide-details="auto" class="modern-input" :disabled="incluirValid"
                prepend-inner-icon="mdi-text" />
            </v-col>
          </v-row>
          <div class="d-flex" style="gap:10px; margin-top:20px;">
            <v-btn color="primary" rounded elevation="0" class="font-weight-bold px-6"
              :disabled="!incluirValid" @click="incluir()">
              <v-icon left size="16">mdi-plus</v-icon>Nuevo
            </v-btn>
            <v-btn color="success" rounded elevation="0" class="font-weight-bold px-6"
              :disabled="!guardarValid" @click="save()">
              <v-icon left size="16">mdi-content-save</v-icon>Guardar
            </v-btn>
            <v-btn color="grey" text rounded class="font-weight-bold px-6"
              :disabled="!cancelarValid" @click="cerrar()">
              Cancelar
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </div>

    <Snackbar :text="snackText" :snackbar="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "SaldoEntrada",
  components: { Snackbar },
  data() {
    return {
      tab: 0, valid: false, incluirValid: true, cancelarValid: false,
      guardarValid: false, validUsuarios: false, search: "", snackText: "", snackValue: false,
      dateModal: false, dates: [], datesFormat: "",
      items: [], usuarios: [], editedIndex: -1,
      fecha: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
      nameRules: [(v) => !!v || "Campo requerido"],
      headers: [
        { text: "ID",          value: "id",          width: 70 },
        { text: "Vendedor",    value: "vendedor"     },
        { text: "Monto",       value: "monto"        },
        { text: "Descripción", value: "descripcion"  },
        { text: "Fecha",       value: "fecha"        },
        { text: "",            value: "actions", sortable: false, width: 50 },
      ],
      editedItem:  { id:0, companyId:"", idUsuario:"", fecha:(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(), monto:0, tipo:1, descripcion:"" },
      defaultItem: { id:0, companyId:"", idUsuario:"", fecha:(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(), monto:0, tipo:1, descripcion:"" },
    };
  },
  computed: { ...mapGetters(["user", "strCia"]) },
  created() {
    this.setDates();
    this.getData();
    this.getUsuariosPorCia();
  },
  methods: {
    getData() {
      if (!this.dates[0] || !this.dates[1]) return;
      axios.get(`api/Tiempos/GetSaldoMovimientos?companyId=${this.strCia}&tipo=1&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`)
        .then(r => { this.items = r.data; }).catch(e => console.log(e));
    },
    getUsuariosPorCia() {
      axios.get(`api/Usuarios/getUsuariosPorCia?companyId=${this.strCia}&usuarioId=${this.user.id}`)
        .then(r => {
          this.usuarios = r.data.filter(u => u.cedula && u.cedula !== "0");
          const me = this.usuarios.find(u => u.cedula === this.user.cedula);
          if (me && me.tipo === true) this.validUsuarios = true;
        }).catch(e => console.log(e));
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.tab = 1; this.cancelarValid = false; this.guardarValid = false; this.incluirValid = true;
    },
    save() {
      this.editedItem.fecha = new Date();
      this.editedItem.companyId = this.strCia;
      axios.post("api/Tiempos/InsertSaldoMovimientos", this.editedItem)
        .then(() => { this.getData(); this.snackText = "✅ Entrada registrada"; this.snackValue = true; this.cerrar(); })
        .catch(() => { this.snackText = "❌ Error inesperado"; this.snackValue = true; });
      this.cancelarValid = false; this.guardarValid = false; this.incluirValid = true;
    },
    incluir() {
      this.$nextTick(() => {
        this.editedIndex = -1;
        this.editedItem = Object.assign({}, this.defaultItem);
        this.cancelarValid = true; this.guardarValid = true; this.incluirValid = false;
      });
    },
    cerrar() {
      this.$nextTick(() => {
        this.editedIndex = -1; this.editedItem = Object.assign({}, this.defaultItem);
        this.cancelarValid = false; this.guardarValid = false; this.incluirValid = true;
      });
    },
    limpiar() { this.snackValue = false; },
    localDate(d = new Date()) {
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    },
    setDates() {
      const d = new Date();
      const ini = new Date(d.getFullYear(), d.getMonth(), 1);
      this.dates = [this.localDate(ini), this.localDate(d)];
      this.setDatesFormat();
    },
    setDatesFormat() {
      if (this.dates.length < 2) return;
      const fmt = (s) => { const [y,m,dd] = s.split('-'); return `${dd}-${m}-${y}`; };
      this.datesFormat = `${fmt(this.dates[0])} ~ ${fmt(this.dates[1])}`;
    },
    fmt(v) { return v ? Number(v).toLocaleString("es-CR") : "0"; },
    fmtFecha(v) { if (!v) return "—"; return new Date(v).toLocaleDateString("es-CR"); },
  },
};
</script>

<style scoped>
.mov-page { padding: 20px 24px; width: 100%; }
.page-header { animation: fadeIn 0.4s ease; }
.header-icon-pill {
  width: 46px; height: 46px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(25,118,210,0.35); flex-shrink: 0;
}
.entrada { background: linear-gradient(135deg, #059669, #34D399); box-shadow: 0 4px 14px rgba(5,150,105,0.35) !important; }
.page-title { font-size: 1.3rem; font-weight: 800; letter-spacing: -0.3px; }
.page-subtitle { font-size: 0.8rem; color: #64748b; }
.theme--dark .page-subtitle { color: #94a3b8; }

/* Tabs pill */
.tabs-pill {
  display: inline-flex; background: rgba(0,0,0,0.06); border-radius: 50px; padding: 4px; gap: 4px;
}
.theme--dark .tabs-pill { background: rgba(255,255,255,0.08); }
.tab-btn {
  padding: 8px 20px; border-radius: 50px; border: none; cursor: pointer;
  font-size: 0.82rem; font-weight: 600; color: #64748b;
  background: transparent; transition: all 0.2s ease; display: flex; align-items: center;
}
.tab-btn:hover { color: #1e293b; }
.theme--dark .tab-btn { color: #94a3b8; }
.active-entrada { background: linear-gradient(135deg, #059669, #34D399) !important; color: white !important; box-shadow: 0 3px 10px rgba(5,150,105,0.35); }

/* Glass */
.glass-card {
  background: rgba(255,255,255,0.75) !important;
  backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255,255,255,0.5) !important;
}
.theme--dark .glass-card { background: rgba(30,30,46,0.75) !important; border-color: rgba(255,255,255,0.08) !important; }

.section-pill {
  width: 26px; height: 26px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.entrada-pill { background: linear-gradient(135deg, #059669, #34D399); }
.section-title { font-size: 0.9rem; font-weight: 700; }

/* Table */
.modern-table >>> tbody tr:hover { background: rgba(5,150,105,0.04) !important; }
.modern-table >>> th {
  font-size: 0.72rem !important; font-weight: 700 !important;
  text-transform: uppercase; letter-spacing: 0.5px;
  color: #64748b !important; border-bottom: 1px solid rgba(0,0,0,0.06) !important;
}
.theme--dark .modern-table >>> th { color: #94a3b8 !important; }
.monto-entrada { color: #059669; font-size: 0.95rem; }
.theme--dark .monto-entrada { color: #34D399; }

/* Input */
.modern-input >>> .v-input__slot { background: rgba(255,255,255,0.6) !important; transition: all 0.2s; }
.modern-input >>> .v-input__slot:hover { background: rgba(255,255,255,0.95) !important; }
.theme--dark .modern-input >>> .v-input__slot { background: rgba(0,0,0,0.2) !important; }

@keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
</style>